// TeamLogoPicker.js — a clickable TeamLogo that opens a file picker, has the
// coach frame the logo in a circle (TeamLogoCropper, 2026-10-19) and hands the
// framed square to `onUpload`. Owns only the picking + busy/error
// UI; the caller owns the actual presign→PUT→persist sequence (it differs
// by tier: getCoachTeamLogoUploadUrl+updateCoachTeam vs.
// getLeagueTeamLogoUploadUrl+updateLeagueTeam) and should reload its team
// list on success so the new logoUrl round-trips back down as a prop.
import { useRef, useState } from "react";
import TeamLogo from "./TeamLogo";
import TeamLogoCropper from "./TeamLogoCropper";

export default function TeamLogoPicker({ name, logoUrl, size = 56, onUpload }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file next time
    if (!file) return;
    setError(null);
    setPicked(file);
  };

  const handleCropped = async (file) => {
    setPicked(null);
    setUploading(true);
    setError(null);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ position: "relative", display: "inline-flex", flexDirection: "column" }}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        aria-label="Change team logo"
        style={{
          border: "none",
          background: "none",
          padding: 0,
          cursor: uploading ? "default" : "pointer",
          position: "relative",
          opacity: uploading ? 0.6 : 1,
        }}
      >
        <TeamLogo name={name} logoUrl={logoUrl} size={size} />
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: -2,
            right: -2,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
          }}
        >
          ✏️
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleChange}
      />
      {picked && (
        <TeamLogoCropper imageFile={picked} onCancel={() => setPicked(null)} onCrop={handleCropped} />
      )}
      {error && (
        <div className="client-error" style={{ marginTop: 4, fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}
